import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Plus, Package, DollarSign, Clock, ChefHat,
  CheckCircle, Utensils, Store, Filter, BarChart3, Star, TrendingUp, Users, AlertTriangle
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import VendorOrderCard from '../components/VendorOrderCard';
import MenuManager from '../components/MenuManager';
import TruckForm from '../components/TruckForm';
import CancelOrderModal from '../components/CancelOrderModal';
import SkipTheLineSettings from '../components/SkipTheLineSettings';
import PayoutEmailSettings from '../components/PayoutEmailSettings';
import TruckLocationSettings from '../components/TruckLocationSettings';
import ComplaintsPanel from '../components/ComplaintsPanel';
import OperatingHoursEditor, {
  hoursStateFromTruck,
  truckHoursFromState,
} from '../components/OperatingHoursEditor';
import { apiFetch, getSessionToken } from '../lib/apiClient';
import {
  formatOrderNumberLabel,
  isPickupAfterToday,
  isPickupTodayOrEarlier,
} from '../lib/orderUtils';
import {
  cancelOrderLocalNotifications,
  notifyNewOrder,
  notifyVendorSla,
  NOTIF_KIND_NEW,
  NOTIF_KIND_NOT_READY,
  NOTIF_KIND_UNAPPROVED,
  requestNotificationPermission,
} from '../lib/notifications';
import {
  getPushPermissionStatus,
  initRemotePush,
} from '../lib/pushNotifications';
import VendorAlertBell from '../components/VendorAlertBell';
import { Capacitor } from '@capacitor/core';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
const PAYOUT_PERIODS = [
  { id: 'day', label: 'Today' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
];
// 'preparing' only exists on orders from before the prep step was removed.
// Live chips (upcoming/active/ready) are pickup-day scoped: today + overdue only.
// Future pickups land in "Later" until their day.
const LIVE_STATUSES = ['pending_vendor_approval', 'confirmed', 'preparing', 'ready'];
const ORDER_FILTERS = [
  { id: 'upcoming', label: 'Upcoming', title: 'Awaiting your approval', statuses: ['pending_vendor_approval'], dayScope: 'today' },
  { id: 'active', label: 'Active', title: 'Approved — not ready yet', statuses: ['confirmed', 'preparing'], dayScope: 'today' },
  { id: 'ready', label: 'Ready for Pickup', title: 'Ready for pickup', statuses: ['ready'], dayScope: 'today' },
  { id: 'later', label: 'Later', title: 'Future pickup days', statuses: LIVE_STATUSES, dayScope: 'later' },
  { id: 'completed', label: 'Completed', title: 'Picked up', statuses: ['picked_up'], newestFirst: true },
  { id: 'cancelled', label: 'Cancelled', title: 'Cancelled', statuses: ['cancelled'], newestFirst: true },
];

export default function VendorDashboard() {
  const navigate = useNavigate();
  const { vendorId: viewAsVendorId } = useParams();
  const { user, updateUserRole, checkAuth } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [payoutSummaryDay, setPayoutSummaryDay] = useState(null);
  const [payoutOrders, setPayoutOrders] = useState([]);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('day');
  const [viewAsVendorName, setViewAsVendorName] = useState('');
  const [vendorReviews, setVendorReviews] = useState([]);
  const [vendorComplaints, setVendorComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorStatus, setVendorStatus] = useState(null);
  const [showTruckForm, setShowTruckForm] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [orderFilter, setOrderFilter] = useState('upcoming');
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [hoursState, setHoursState] = useState(() => hoursStateFromTruck({}));
  const [maxOrdersPerSlot, setMaxOrdersPerSlot] = useState(10);
  const [savingHours, setSavingHours] = useState(false);
  const [urgencyAlerts, setUrgencyAlerts] = useState([]);
  const [pushPermission, setPushPermission] = useState('unknown');
  const [pushBusy, setPushBusy] = useState(false);
  const announcedOrderIdsRef = useRef(new Set());
  const slaNudgedRef = useRef(new Set());
  const dismissedUrgencyRef = useRef(new Set());
  const loadedTruckIdsRef = useRef(new Set());
  const dataRequestIdRef = useRef(0);
  const pushAutoTriedRef = useRef(false);
  const selectedTruckId = selectedTruck?.truck_id || null;
  const selectedTruckIdRef = useRef(selectedTruckId);
  selectedTruckIdRef.current = selectedTruckId;

  const pushUrgencyAlert = useCallback((alert) => {
    if (!alert?.orderId || !alert?.kind) return;
    const key = `${alert.orderId}:${alert.kind}`;
    if (dismissedUrgencyRef.current.has(key)) return;
    setUrgencyAlerts((prev) => {
      if (prev.some((a) => a.orderId === alert.orderId && a.kind === alert.kind)) return prev;
      return [...prev, alert];
    });
  }, []);

  const dismissUrgencyAlert = useCallback((alert) => {
    if (!alert?.orderId || !alert?.kind) return;
    dismissedUrgencyRef.current.add(`${alert.orderId}:${alert.kind}`);
    setUrgencyAlerts((prev) =>
      prev.filter((a) => !(a.orderId === alert.orderId && a.kind === alert.kind))
    );
  }, []);

  const clearUrgencyForOrder = useCallback((orderId, kinds = null) => {
    if (!orderId) return;
    setUrgencyAlerts((prev) =>
      prev.filter((a) => {
        if (a.orderId !== orderId) return true;
        if (kinds && !kinds.includes(a.kind)) return true;
        dismissedUrgencyRef.current.add(`${a.orderId}:${a.kind}`);
        return false;
      })
    );
  }, []);

  const clearAllUrgencyAlerts = useCallback(() => {
    setUrgencyAlerts((prev) => {
      prev.forEach((a) => {
        if (a?.orderId && a?.kind) {
          dismissedUrgencyRef.current.add(`${a.orderId}:${a.kind}`);
        }
      });
      return [];
    });
  }, []);

  const adminViewMode = user?.role === 'admin' && !!viewAsVendorId;
  const vendorQuery = viewAsVendorId ? `?vendor_id=${encodeURIComponent(viewAsVendorId)}` : '';
  const isVendor = user?.role === 'vendor' || user?.role === 'admin';

  useEffect(() => {
    if (!selectedTruck) return;
    setHoursState(hoursStateFromTruck(selectedTruck.operating_hours || {}));
    setMaxOrdersPerSlot(selectedTruck.max_orders_per_slot ?? 10);
  }, [selectedTruck?.truck_id, selectedTruck?.operating_hours, selectedTruck?.max_orders_per_slot]);

  // Pick up role changes (e.g. admin assigned this user as truck staff)
  useEffect(() => {
    if (checkAuth) checkAuth();
  }, [checkAuth]);

  // Check vendor status on mount
  useEffect(() => {
    const checkVendorStatus = async () => {
      if (!isVendor && !adminViewMode) return;
      try {
        const response = await apiFetch('/api/vendor/status');
        if (response.ok) {
          const status = await response.json();
          setVendorStatus(status);
          
          // If no truck, redirect to registration
          if (!status.has_truck && user?.role !== 'admin' && !adminViewMode) {
            navigate('/vendor/register');
            return;
          }
        } else {
          toast.error(`Failed to load vendor status (${response.status})`);
        }
      } catch (error) {
        console.error('Failed to check vendor status:', error);
        toast.error('Failed to load vendor status');
      }
    };

    checkVendorStatus();
  }, [isVendor, navigate, user?.role, adminViewMode]);

  const fetchTrucks = useCallback(async () => {
    try {
      const trucksRes = await apiFetch(`/api/vendor/trucks${vendorQuery}`);
      if (!trucksRes.ok) {
        toast.error(`Failed to load trucks (${trucksRes.status})`);
        return;
      }
      const trucksData = await trucksRes.json();
      setTrucks(trucksData);
      setSelectedTruck((prev) => {
        if (trucksData.length === 0) return null;
        const next = trucksData.find((t) => t.truck_id === prev?.truck_id) || trucksData[0];
        // Keep the same object when unchanged so the Hours editor doesn't reset mid-edit.
        return prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      });
      if (trucksData.length === 0) setLoading(false);
    } catch (error) {
      console.error('Failed to fetch trucks:', error);
      toast.error('Failed to load trucks');
    }
  }, [vendorQuery]);

  const fetchData = useCallback(async () => {
    if (!selectedTruckId) return;
    const requestId = ++dataRequestIdRef.current;
    const scoped = (params = {}) => {
      const qs = new URLSearchParams({ ...params, truck_id: selectedTruckId });
      if (viewAsVendorId) qs.set('vendor_id', viewAsVendorId);
      return `?${qs.toString()}`;
    };
    const readJson = async (res) => (res.ok ? res.json() : null);
    try {
      const responses = await Promise.all([
        apiFetch(`/api/vendor/orders${scoped()}`),
        apiFetch(`/api/vendor/stats${scoped()}`),
        apiFetch(`/api/vendor/stats/insights${scoped()}`),
        apiFetch(`/api/vendor/reviews${scoped()}`),
        apiFetch(`/api/vendor/payout-summary${scoped({ period: analyticsPeriod })}`),
        apiFetch(`/api/vendor/payout-summary${scoped({ period: 'day' })}`),
        apiFetch(`/api/vendor/stats/orders${scoped({ period: analyticsPeriod })}`),
        apiFetch(`/api/vendor/complaints${scoped()}`),
      ]);
      const [ordersRes, analyticsRes] = responses;
      const [ordersData, analyticsData, insightsData, reviewsData, payoutData, payoutDayData, payoutOrdersData, complaintsData] =
        await Promise.all(responses.map(readJson));
      // A newer request (e.g. after switching trucks) owns the state now.
      if (requestId !== dataRequestIdRef.current) return;

      if (ordersData) {
        const announced = announcedOrderIdsRef.current;
        const firstLoad = !loadedTruckIdsRef.current.has(selectedTruckId);
        ordersData.forEach((o) => {
          if (announced.has(o.order_id)) return;
          announced.add(o.order_id);
          // Orders that arrived while the live socket was down still get an alert.
          if (!firstLoad && !adminViewMode && o.status === 'pending_vendor_approval') {
            const label = formatOrderNumberLabel(o);
            const title = 'New SkipdQ order';
            const body = `Order ${label}`;
            pushUrgencyAlert({
              orderId: o.order_id,
              kind: NOTIF_KIND_NEW,
              title,
              body,
            });
            notifyNewOrder({ title, body, orderId: o.order_id });
          }
        });
        loadedTruckIdsRef.current.add(selectedTruckId);
        setOrders(ordersData);
      } else {
        toast.error(`Failed to load orders (${ordersRes.status})`);
      }

      if (analyticsData) setAnalytics(analyticsData);
      else toast.error(`Failed to load analytics (${analyticsRes.status})`);

      if (insightsData) setInsights(insightsData);
      if (reviewsData) setVendorReviews(reviewsData.reviews || []);
      if (Array.isArray(complaintsData)) setVendorComplaints(complaintsData);
      if (payoutData) setPayoutSummary(payoutData);
      if (payoutDayData) setPayoutSummaryDay(payoutDayData);
      if (payoutOrdersData) setPayoutOrders(payoutOrdersData.orders || []);
      if (adminViewMode && viewAsVendorId) {
        const vendorsRes = await apiFetch('/api/admin/vendors');
        if (vendorsRes.ok) {
          const vendorsData = await vendorsRes.json();
          const match = (vendorsData.vendors || []).find((v) => v.vendor_id === viewAsVendorId);
          setViewAsVendorName(match?.vendor_name || viewAsVendorId);
        }
      }
    } catch (error) {
      if (requestId !== dataRequestIdRef.current) return;
      console.error('Failed to fetch vendor data:', error);
      toast.error('Failed to fetch vendor data');
    } finally {
      if (requestId === dataRequestIdRef.current) setLoading(false);
    }
  }, [selectedTruckId, vendorQuery, analyticsPeriod, adminViewMode, viewAsVendorId, pushUrgencyAlert]);

  // Clear the previous truck's numbers so they never show under the new truck.
  useEffect(() => {
    if (!selectedTruckId) return;
    setLoading(true);
    setOrders([]);
    setAnalytics(null);
    setInsights(null);
    setVendorReviews([]);
    setVendorComplaints([]);
    setPayoutSummary(null);
    setPayoutSummaryDay(null);
    setPayoutOrders([]);
  }, [selectedTruckId]);

  const canLoadDashboard = isVendor && (
    adminViewMode
    || (vendorStatus !== null && (vendorStatus?.is_approved || user?.role === 'admin'))
  );

  useEffect(() => {
    if (!canLoadDashboard) return;
    fetchTrucks();
    const interval = setInterval(fetchTrucks, 30000);
    return () => clearInterval(interval);
  }, [canLoadDashboard, fetchTrucks]);

  useEffect(() => {
    if (!canLoadDashboard || !selectedTruckId) return;
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [canLoadDashboard, selectedTruckId, fetchData]);

  const refreshPushPermission = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      setPushPermission('unavailable');
      return 'unavailable';
    }
    const status = await getPushPermissionStatus();
    setPushPermission(status);
    return status;
  }, []);

  const enablePushNotifications = useCallback(async () => {
    if (adminViewMode) return;
    setPushBusy(true);
    try {
      // Local channel + system prompt (user-gesture safe when called from button).
      await requestNotificationPermission();
      const result = await initRemotePush({ interactive: true });
      const status = await refreshPushPermission();
      if (result?.ok || status === 'granted') {
        toast.success('Notifications enabled');
      } else if (result?.reason === 'denied' || status === 'denied') {
        toast.error('Notifications blocked — enable them in iPhone Settings → SkipdQ');
      } else {
        toast.error('Could not enable notifications — try again');
      }
    } catch (e) {
      console.warn('[Push] enablePushNotifications failed', e);
      toast.error('Could not enable notifications');
    } finally {
      setPushBusy(false);
    }
  }, [adminViewMode, refreshPushPermission]);

  // After dashboard is stable (not mid-OAuth), try once interactively if not granted.
  useEffect(() => {
    if (!canLoadDashboard || adminViewMode) return undefined;
    if (!selectedTruckId) return undefined;
    if (!vendorStatus?.is_approved && user?.role !== 'admin') return undefined;
    if (!Capacitor.isNativePlatform()) return undefined;
    if (pushAutoTriedRef.current) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      pushAutoTriedRef.current = true;
      const status = await refreshPushPermission();
      if (cancelled) return;
      if (status === 'granted') {
        await initRemotePush({ interactive: false });
        return;
      }
      // Prompt once while vendor is looking at the dashboard (app foreground).
      await enablePushNotifications();
    }, 1200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    canLoadDashboard,
    adminViewMode,
    selectedTruckId,
    vendorStatus?.is_approved,
    user?.role,
    refreshPushPermission,
    enablePushNotifications,
  ]);

  // One-shot SLA nudges while the dashboard is open (remote APNs/FCM covers background).
  // Per order: at most T−30 unapproved + T−15 not-ready (plus the separate new-order ping).
  useEffect(() => {
    if (!canLoadDashboard || adminViewMode) return undefined;

    const tick = () => {
      const now = Date.now();
      orders.forEach((o) => {
        if (!isPickupTodayOrEarlier(o)) return;
        const pickupMs = new Date(o.scheduled_pickup_time).getTime();
        if (Number.isNaN(pickupMs)) return;
        const minsToPickup = (pickupMs - now) / 60000;

        if (
          o.status === 'pending_vendor_approval' &&
          minsToPickup <= 30 &&
          minsToPickup > -120
        ) {
          const key = `${o.order_id}:unapproved30`;
          if (!slaNudgedRef.current.has(key)) {
            slaNudgedRef.current.add(key);
            const label = formatOrderNumberLabel(o);
            const title = 'Approve soon';
            const body = `${label} — pickup in under 30 minutes`;
            pushUrgencyAlert({
              orderId: o.order_id,
              kind: NOTIF_KIND_UNAPPROVED,
              title,
              body,
            });
            notifyVendorSla({
              title,
              body,
              orderId: o.order_id,
              kind: NOTIF_KIND_UNAPPROVED,
            });
          }
        }

        if (['confirmed', 'preparing'].includes(o.status)) {
          const readyByMs = o.vendor_ready_by
            ? new Date(o.vendor_ready_by).getTime()
            : pickupMs - 5 * 60 * 1000;
          const minsToReady = (readyByMs - now) / 60000;
          if (minsToPickup <= 15 || minsToReady <= 15) {
            const key = `${o.order_id}:notready15`;
            if (!slaNudgedRef.current.has(key)) {
              slaNudgedRef.current.add(key);
              const label = formatOrderNumberLabel(o);
              const title = 'Mark order ready';
              const body = `${label} — less than 15 minutes to pickup`;
              pushUrgencyAlert({
                orderId: o.order_id,
                kind: NOTIF_KIND_NOT_READY,
                title,
                body,
              });
              notifyVendorSla({
                title,
                body,
                orderId: o.order_id,
                kind: NOTIF_KIND_NOT_READY,
              });
            }
          }
        }
      });
    };

    tick();
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);
  }, [orders, canLoadDashboard, adminViewMode, pushUrgencyAlert]);

  // One live-orders socket per truck so owners of several trucks hear about all of them.
  const truckIdsKey = trucks.map((t) => t.truck_id).sort().join(',');
  useEffect(() => {
    if (!truckIdsKey) return;
    if (!adminViewMode && !vendorStatus?.is_approved) return;

    let disposed = false;
    const sockets = new Map();
    const timers = new Map();

    const handleMessage = (socketTruckId, event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.type === 'new_order' && data.order?.order_id) {
        const orderId = data.order.order_id;
        if (announcedOrderIdsRef.current.has(orderId)) return;
        announcedOrderIdsRef.current.add(orderId);
        const orderTruckId = data.order.truck_id || socketTruckId;
        if (orderTruckId === selectedTruckIdRef.current) {
          setOrders((prev) => (
            prev.some((o) => o.order_id === orderId) ? prev : [data.order, ...prev]
          ));
        }
        const truckLabel = trucks.length > 1 && data.order.truck_name ? ` · ${data.order.truck_name}` : '';
        const title = 'New SkipdQ order';
        const body = `Order ${formatOrderNumberLabel(data.order)}${truckLabel}`;
        pushUrgencyAlert({
          orderId,
          kind: NOTIF_KIND_NEW,
          title,
          body,
        });
        notifyNewOrder({ title, body, orderId });
      } else if (data.type === 'order_update') {
        setOrders((prev) => prev.map((o) => (
          o.order_id === data.order_id ? { ...o, status: data.status } : o
        )));
        if (['ready', 'picked_up', 'cancelled'].includes(data.status)) {
          clearUrgencyForOrder(data.order_id);
          cancelOrderLocalNotifications(data.order_id);
        } else if (['confirmed', 'preparing'].includes(data.status)) {
          clearUrgencyForOrder(data.order_id, [NOTIF_KIND_NEW, NOTIF_KIND_UNAPPROVED]);
          cancelOrderLocalNotifications(data.order_id);
        }
      } else if (data.type === 'order_cancelled') {
        setOrders((prev) => prev.map((o) => (
          o.order_id === data.order_id ? { ...o, status: 'cancelled' } : o
        )));
        clearUrgencyForOrder(data.order_id);
        cancelOrderLocalNotifications(data.order_id);
      }
    };

    const connect = (truckId) => {
      if (disposed) return;
      try {
        const token = getSessionToken();
        const ws = new WebSocket(`${WS_URL}/ws/vendor/${truckId}?token=${encodeURIComponent(token)}`);
        sockets.set(truckId, ws);
        ws.onmessage = (event) => handleMessage(truckId, event);
        ws.onerror = () => {
          // onclose handles reconnect; avoid noisy Safari logs
        };
        ws.onclose = () => {
          if (disposed) return;
          timers.set(truckId, setTimeout(() => connect(truckId), 5000));
        };
      } catch {
        if (!disposed) timers.set(truckId, setTimeout(() => connect(truckId), 5000));
      }
    };

    truckIdsKey.split(',').forEach(connect);

    return () => {
      disposed = true;
      timers.forEach((t) => clearTimeout(t));
      sockets.forEach((ws) => {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        // Closing while CONNECTING triggers Safari "closed before established";
        // only close an open socket; otherwise drop handlers and let it die.
        if (ws.readyState === WebSocket.OPEN) ws.close();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckIdsKey, vendorStatus?.is_approved, adminViewMode]);

  const handleBecomeVendor = async () => {
    const success = await updateUserRole('vendor');
    if (success) {
      toast.success('You are now a vendor!');
      navigate('/vendor/register');
    } else {
      toast.error('Failed to update role');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus, pickupCode) => {
    try {
      const body = { status: newStatus };
      if (pickupCode) {
        body.pickup_code = pickupCode;
      }

      const response = await apiFetch(`/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const updated = await response.json().catch(() => ({}));
        setOrders(prev => prev.map(o =>
          o.order_id === orderId ? { ...o, ...updated, status: newStatus } : o
        ));

        if (['confirmed', 'preparing'].includes(newStatus)) {
          clearUrgencyForOrder(orderId, [NOTIF_KIND_NEW, NOTIF_KIND_UNAPPROVED]);
          cancelOrderLocalNotifications(orderId);
        } else if (['ready', 'picked_up', 'cancelled'].includes(newStatus)) {
          clearUrgencyForOrder(orderId);
          cancelOrderLocalNotifications(orderId);
        }

        if (newStatus === 'ready') {
          toast.success('Order marked ready! Student notified.');
        } else if (newStatus === 'picked_up') {
          toast.success('Pickup verified — order complete');
        } else {
          toast.success('Order status updated');
        }
        return true;
      }

      const data = await response.json().catch(() => ({}));
      if (newStatus === 'picked_up') {
        toast.error(data.detail || 'Invalid pickup code, try again');
      } else {
        toast.error(data.detail || 'Failed to update order');
      }
      return false;
    } catch (error) {
      toast.error('Failed to update order');
      return false;
    }
  };

  const handleCancelOrder = async (orderId, reason) => {
    try {
      const response = await apiFetch(`/api/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        const result = await response.json();
        setOrders(prev => prev.map(o => 
          o.order_id === orderId ? result : o
        ));
        clearUrgencyForOrder(orderId);
        cancelOrderLocalNotifications(orderId);
        toast.success('Order cancelled and refund processed');
        setCancellingOrder(null);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel order');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleTruckCreated = (truck) => {
    setTrucks(prev => [...prev, truck]);
    setSelectedTruck(truck);
    setShowTruckForm(false);
    toast.success('Food truck created!');
  };

  // Non-vendor view - redirect to vendor landing
  if (!isVendor) {
    return (
      <div className="min-h-screen bg-muted/30">
          <div
            className="bg-card border-b px-6 pb-4"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
          >
          <div className="max-w-7xl mx-auto flex items-start justify-between">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
          </div>
        </div>
        
        <div className="max-w-md mx-auto px-4 pt-24 text-center">
          <Store className="w-16 h-16 mx-auto text-primary mb-6" />
          <h1 className="text-2xl font-bold mb-4">Become a Vendor</h1>
          <p className="text-muted-foreground mb-8">
            Register your food truck and start receiving orders from students across campus.
          </p>
          <Link to="/vendor">
            <Button size="lg" data-testid="become-vendor-btn">
              Get Started
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Pending approval view
  if (vendorStatus && !vendorStatus.is_approved && vendorStatus.has_truck && user?.role !== 'admin' && !adminViewMode) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div
          className="bg-card border-b px-6 pb-4"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
        >
          <div className="max-w-7xl mx-auto flex items-start justify-between">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
            <h1 className="font-bold text-lg">Vendor Dashboard</h1>
            <div className="w-16" />
          </div>
        </div>
        
        <div className="max-w-md mx-auto px-4 pt-24 text-center">
          <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-yellow-600" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Pending Approval</h1>
          <p className="text-muted-foreground mb-4">
            Your food truck <strong>{vendorStatus.truck?.name}</strong> is pending approval.
          </p>
          <p className="text-sm text-muted-foreground">
            Our team will review your application and notify you once approved. 
            This usually takes 1-2 business days.
          </p>
        </div>
      </div>
    );
  }

  // Live orders are prepended, so sort at render time. Open orders: earliest pickup first;
  // finished orders: most recent first so today's aren't buried under old ones.
  const byPickupTime = (a, b) => new Date(a.scheduled_pickup_time) - new Date(b.scheduled_pickup_time);
  const ordersForFilter = (filterId) => {
    const filter = ORDER_FILTERS.find((f) => f.id === filterId) || ORDER_FILTERS[0];
    const list = orders.filter((o) => {
      if (!filter.statuses.includes(o.status)) return false;
      if (filter.dayScope === 'today') return isPickupTodayOrEarlier(o);
      if (filter.dayScope === 'later') return isPickupAfterToday(o);
      return true;
    }).sort(byPickupTime);
    return filter.newestFirst ? list.reverse() : list;
  };
  const filteredOrders = ordersForFilter(orderFilter);
  const currentFilter = ORDER_FILTERS.find((f) => f.id === orderFilter) || ORDER_FILTERS[0];
  const approveBlocked = orderFilter === 'later';

  const pendingOrders = orders.filter(
    (o) =>
      ['pending_vendor_approval', 'confirmed', 'preparing'].includes(o.status) &&
      isPickupTodayOrEarlier(o)
  );

  // Calculate next pickup countdown
  const getNextPickup = () => {
    const upcoming = orders
      .filter(
        (o) =>
          ['pending_vendor_approval', 'confirmed', 'preparing'].includes(o.status) &&
          isPickupTodayOrEarlier(o)
      )
      .sort((a, b) => new Date(a.scheduled_pickup_time) - new Date(b.scheduled_pickup_time));
    
    if (upcoming.length === 0) return null;
    
    const next = new Date(upcoming[0].scheduled_pickup_time);
    const now = new Date();
    const diff = next - now;
    
    if (diff < 0) return 'Now';
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };

  const handleUpdateComplaint = async (complaint, draft) => {
    try {
      const response = await apiFetch(`/api/vendor/complaints/${complaint.complaint_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Could not update complaint');
      setVendorComplaints((prev) => prev.map((item) => (
        item.complaint_id === data.complaint_id ? data : item
      )));
      toast.success('Complaint updated');
    } catch (error) {
      toast.error(error.message || 'Could not update complaint');
      throw error;
    }
  };

  const handleSaveHours = async () => {
    if (!selectedTruck || adminViewMode) return;
    const operating_hours = truckHoursFromState(hoursState);
    if (Object.keys(operating_hours).length === 0) {
      toast.error('At least one operating day is required');
      return;
    }
    setSavingHours(true);
    try {
      const res = await apiFetch(`/api/trucks/${selectedTruck.truck_id}/hours`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operating_hours,
          max_orders_per_slot: maxOrdersPerSlot,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to save hours');
      }
      setSelectedTruck(data);
      setTrucks((prev) => prev.map((t) => (t.truck_id === data.truck_id ? data : t)));
      toast.success('Operating hours updated');
    } catch (err) {
      toast.error(err.message || 'Failed to save hours');
    } finally {
      setSavingHours(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 overflow-x-clip">
      {/* Header */}
      <div
        className="bg-card border-b px-6 pb-4 sticky z-50"
        style={{ top: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="max-w-7xl mx-auto flex items-start justify-between gap-2 min-w-0">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground shrink-0">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="font-bold text-lg truncate">Vendor Dashboard</h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!adminViewMode && (
              <VendorAlertBell
                alerts={urgencyAlerts}
                onDismiss={dismissUrgencyAlert}
                onClearAll={clearAllUrgencyAlerts}
              />
            )}
            {trucks.length > 0 && (
              <select 
                className="bg-muted rounded-lg px-3 py-2 text-sm max-w-[8rem] sm:max-w-none"
                value={selectedTruck?.truck_id || ''}
                onChange={(e) => {
                  const truck = trucks.find(t => t.truck_id === e.target.value);
                  setSelectedTruck(truck);
                }}
                data-testid="truck-selector"
              >
                {trucks.map(t => (
                  <option key={t.truck_id} value={t.truck_id}>
                    {t.name}{t.access_role === 'staff' ? ' (Staff)' : ''}
                  </option>
                ))}
              </select>
            )}
            {!adminViewMode && trucks.some((t) => t.access_role !== 'staff') && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => navigate('/vendor/register')}
                data-testid="add-truck-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Truck
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto w-full min-w-0 px-6 py-6">
        {adminViewMode && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2">
            <span>
              <strong>Admin view</strong> — {viewAsVendorName || 'Vendor'} (read-only)
            </span>
            <Link to="/admin" className="text-primary font-medium hover:underline">
              Back to Admin
            </Link>
          </div>
        )}
        {!adminViewMode && Capacitor.isNativePlatform() && pushPermission !== 'granted' && pushPermission !== 'unavailable' && (
          <div
            className="mb-4 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3"
            data-testid="vendor-enable-notifications"
          >
            <div className="min-w-0">
              <p className="font-semibold">Enable order notifications</p>
              <p className="text-muted-foreground text-xs mt-0.5">
                Allow SkipdQ to alert you when new orders arrive — even if the app is closed.
              </p>
            </div>
            <Button
              size="sm"
              disabled={pushBusy}
              onClick={enablePushNotifications}
              data-testid="enable-notifications-btn"
            >
              {pushBusy ? 'Enabling…' : 'Enable notifications'}
            </Button>
          </div>
        )}
        {/* Analytics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-4 border"
          >
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Package className="w-4 h-4" />
              <span className="text-xs">Today's Orders</span>
            </div>
            <p className="text-2xl font-bold" data-testid="today-orders">
              {analytics?.today_orders || 0}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-xl p-4 border"
          >
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs">Fulfilled Today</span>
            </div>
            <p className="text-2xl font-bold text-green-600" data-testid="today-fulfilled">
              {payoutSummaryDay?.orders_fulfilled ?? 0}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-xl p-4 border"
          >
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <ChefHat className="w-4 h-4" />
              <span className="text-xs">Pending</span>
            </div>
            <p className="text-2xl font-bold text-orange-600" data-testid="pending-orders">
              {analytics?.pending_orders || 0}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card rounded-xl p-4 border"
          >
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs">Today's Revenue</span>
            </div>
            <p className="text-2xl font-bold text-green-600" data-testid="today-revenue">
              CA$ {(payoutSummaryDay?.revenue_incl_tax ?? 0).toFixed(2)}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card rounded-xl p-4 border"
          >
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Clock className="w-4 h-4" />
              <span className="text-xs">Next Pickup</span>
            </div>
            <p className="text-2xl font-bold text-primary" data-testid="next-pickup">
              {getNextPickup() || '-'}
            </p>
          </motion.div>
        </div>

        {/* Main Content */}
        {trucks.length === 0 ? (
          <div className="bg-card rounded-xl p-12 text-center border">
            <Utensils className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-bold mb-2">No Food Trucks Yet</h2>
            <p className="text-muted-foreground mb-6">
              Create your first food truck to start receiving orders
            </p>
            <Button onClick={() => setShowTruckForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Food Truck
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="orders" className="space-y-6 min-w-0">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="orders" className="gap-2">
                <Package className="w-4 h-4" />
                Orders
                {pendingOrders.length > 0 && (
                  <Badge variant="destructive" className="ml-1">
                    {pendingOrders.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="menu" className="gap-2">
                <Utensils className="w-4 h-4" />
                Menu
              </TabsTrigger>
              <TabsTrigger value="hours" className="gap-2">
                <Clock className="w-4 h-4" />
                Hours & Location
              </TabsTrigger>
              <TabsTrigger value="analytics" className="gap-2">
                <BarChart3 className="w-4 h-4" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="reviews" className="gap-2">
                <Star className="w-4 h-4" />
                Reviews
                {vendorReviews.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{vendorReviews.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="complaints" className="gap-2" data-testid="vendor-complaints-tab">
                <AlertTriangle className="w-4 h-4" />
                Complaints
                {vendorComplaints.filter((c) => c.status === 'open').length > 0 && (
                  <Badge variant="destructive" className="ml-1">
                    {vendorComplaints.filter((c) => c.status === 'open').length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="orders" className="space-y-6">
              {/* Order Filters */}
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-muted-foreground" />
                {ORDER_FILTERS.map((filter) => {
                  const count = ordersForFilter(filter.id).length;
                  return (
                    <Button
                      key={filter.id}
                      variant={orderFilter === filter.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setOrderFilter(filter.id)}
                      data-testid={`order-filter-${filter.id}`}
                    >
                      {filter.label}
                      {count > 0 && <span className="ml-1.5 opacity-80">({count})</span>}
                    </Button>
                  );
                })}
              </div>

              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  {orderFilter === 'ready'
                    ? <CheckCircle className="w-5 h-5 text-green-600" />
                    : <ChefHat className="w-5 h-5 text-orange-600" />}
                  {currentFilter.title} ({filteredOrders.length})
                </h3>
                {filteredOrders.length === 0 ? (
                  <div className="bg-card rounded-xl p-8 text-center border">
                    <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">{loading ? 'Loading orders…' : 'No orders'}</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredOrders.map(order => (
                      <VendorOrderCard 
                        key={order.order_id} 
                        order={order}
                        onUpdateStatus={handleUpdateOrderStatus}
                        onCancel={() => setCancellingOrder(order)}
                        approveBlocked={approveBlocked || isPickupAfterToday(order)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="menu">
              {selectedTruck && (
                adminViewMode ? (
                  <div className="bg-card rounded-xl p-6 border text-muted-foreground text-sm">
                    Menu is read-only in admin view. Switch to the vendor account to edit items.
                  </div>
                ) : (
                  <MenuManager truck={selectedTruck} />
                )
              )}
            </TabsContent>

            <TabsContent value="hours" className="space-y-4">
              {selectedTruck && (
                adminViewMode ? (
                  <div className="space-y-4">
                    <div className="bg-card rounded-xl p-6 border text-muted-foreground text-sm">
                      Hours are read-only in admin view. Switch to the vendor account to edit schedule.
                    </div>
                    <TruckLocationSettings
                      truck={selectedTruck}
                      onSaved={(data) => {
                        const merged = { ...data, access_role: selectedTruck.access_role };
                        setSelectedTruck(merged);
                        setTrucks((prev) => prev.map((t) => (t.truck_id === merged.truck_id ? merged : t)));
                      }}
                    />
                    <SkipTheLineSettings truck={selectedTruck} readOnly />
                    <PayoutEmailSettings truck={selectedTruck} readOnly />
                  </div>
                ) : (
                  <div className="space-y-4">
                  <div className="bg-card rounded-xl border p-4 md:p-6 space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg">Operating Hours</h3>
                      <p className="text-sm text-muted-foreground">
                        Students can only book pickup slots while you are open.
                      </p>
                    </div>
                    <OperatingHoursEditor
                      hours={hoursState}
                      onChange={setHoursState}
                      maxOrdersPerSlot={maxOrdersPerSlot}
                      onMaxOrdersChange={setMaxOrdersPerSlot}
                    />
                    <Button
                      onClick={handleSaveHours}
                      disabled={savingHours}
                      data-testid="save-hours-btn"
                    >
                      {savingHours ? 'Saving…' : 'Save Hours'}
                    </Button>
                  </div>
                  <TruckLocationSettings
                    truck={selectedTruck}
                    onSaved={(data) => {
                      const merged = { ...data, access_role: selectedTruck.access_role };
                      setSelectedTruck(merged);
                      setTrucks((prev) => prev.map((t) => (t.truck_id === merged.truck_id ? merged : t)));
                    }}
                  />
                  <SkipTheLineSettings
                    truck={selectedTruck}
                    onSaved={(data) => {
                      setSelectedTruck(data);
                      setTrucks((prev) => prev.map((t) => (t.truck_id === data.truck_id ? data : t)));
                    }}
                  />
                  <PayoutEmailSettings
                    truck={selectedTruck}
                    readOnly={selectedTruck.access_role === 'staff'}
                    onSaved={(data) => {
                      const merged = { ...data, access_role: selectedTruck.access_role };
                      setSelectedTruck(merged);
                      setTrucks((prev) => prev.map((t) => (t.truck_id === merged.truck_id ? merged : t)));
                    }}
                  />
                  </div>
                )
              )}
            </TabsContent>

            <TabsContent value="analytics" className="space-y-6">
              <div className="flex flex-wrap gap-2">
                {PAYOUT_PERIODS.map((p) => (
                  <Button
                    key={p.id}
                    size="sm"
                    variant={analyticsPeriod === p.id ? 'default' : 'outline'}
                    onClick={() => setAnalyticsPeriod(p.id)}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>

              {!payoutSummary ? (
                <div className="bg-card rounded-xl p-8 text-center border text-muted-foreground">
                  Loading analytics…
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-card rounded-xl p-4 border">
                      <p className="text-xs text-muted-foreground mb-1">Revenue (incl. tax)</p>
                      <p className="text-3xl font-bold text-green-600">
                        CA$ {(payoutSummary.revenue_incl_tax || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-card rounded-xl p-4 border">
                      <p className="text-xs text-muted-foreground mb-1">Orders requested</p>
                      <p className="text-3xl font-bold">{payoutSummary.orders_requested || 0}</p>
                    </div>
                    <div className="bg-card rounded-xl p-4 border">
                      <p className="text-xs text-muted-foreground mb-1">Orders fulfilled</p>
                      <p className="text-3xl font-bold">{payoutSummary.orders_fulfilled || 0}</p>
                    </div>
                  </div>

                  <div className="bg-card rounded-xl p-4 border">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      Order volume
                    </h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={payoutSummary.volume || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [v, 'Orders']} />
                          <Bar dataKey="count" fill="#B00BF0" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bg-card rounded-xl p-4 border">
                    <h3 className="font-semibold mb-4">Order timeline</h3>
                    {payoutOrders.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No orders in this period.</p>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-auto">
                        {payoutOrders.map((row) => (
                          <div key={row.order_id} className="flex flex-wrap items-center justify-between gap-2 border-b pb-2 text-sm">
                            <div>
                              <p className="font-medium">
                                {row.created_at ? new Date(row.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Toronto' }) : '—'}
                                {' — '}{row.items_summary}
                              </p>
                              <p className="text-muted-foreground">{row.student_name}</p>
                            </div>
                            <div className="text-right">
                              <Badge variant="secondary" className="capitalize">{row.status?.replace(/_/g, ' ')}</Badge>
                              {row.status === 'picked_up' && (
                                <p className="text-green-700 font-medium mt-1">
                                  CA$ {(row.revenue_incl_tax || 0).toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {insights && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-card rounded-xl p-4 border">
                        <h3 className="font-semibold mb-4">Most popular items</h3>
                        {(!insights.popular_items || insights.popular_items.length === 0) ? (
                          <p className="text-sm text-muted-foreground">No order data yet.</p>
                        ) : (
                          <div className="space-y-3">
                            {insights.popular_items.map((item, i) => (
                              <div key={item.name || i} className="flex items-center justify-between">
                                <span className="text-sm font-medium">{item.name || 'Unknown'}</span>
                                <span className="text-sm text-muted-foreground">{item.quantity} sold</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="bg-card rounded-xl p-4 border">
                        <h3 className="font-semibold mb-4">Operations compliance</h3>
                        <div className="space-y-2 text-sm">
                          <p>Acceptance rate: {insights.compliance?.acceptance_rate ?? 0}%</p>
                          <p>Ready on time: {insights.compliance?.ready_on_time_rate ?? 0}%</p>
                          <p>Cancellation ratio: {insights.compliance?.cancellation_ratio ?? 0}%</p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="reviews" className="space-y-4">
              {vendorReviews.length === 0 ? (
                <div className="bg-card rounded-xl p-8 text-center border">
                  <Star className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No reviews yet</p>
                </div>
              ) : (
                vendorReviews.map((review) => (
                  <div key={review.review_id} className="bg-card rounded-xl p-4 border">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{review.student_name || 'Student'}</span>
                        {review.truck_name && (
                          <Badge variant="secondary" className="text-xs">{review.truck_name}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${review.rating >= star ? 'fill-primary text-primary' : 'text-muted-foreground'}`}
                          />
                        ))}
                      </div>
                    </div>
                    {review.review_text && (
                      <p className="text-sm text-muted-foreground mt-1">{review.review_text}</p>
                    )}
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="complaints" className="space-y-4">
              <ComplaintsPanel
                complaints={vendorComplaints}
                onUpdate={handleUpdateComplaint}
                testId="vendor-complaints-panel"
              />
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Truck Form Modal */}
      {showTruckForm && (
        <TruckForm 
          onClose={() => setShowTruckForm(false)}
          onCreated={handleTruckCreated}
        />
      )}

      {/* Cancel Order Modal */}
      {cancellingOrder && (
        <CancelOrderModal
          order={cancellingOrder}
          onClose={() => setCancellingOrder(null)}
          onConfirm={handleCancelOrder}
        />
      )}
    </div>
  );
}
